import { apiFetch } from './http';

/**
 * Typed client of `GET /reports` and `GET /reports/{key}` (13 §4.11, AM-130 point 4). Every shape
 * below mirrors `ServiceLedger.Modules.Reporting.Contracts.ReportContracts` field for field, in the
 * camelCase the API serialises with - the C# records are the source of truth, this file only
 * restates them.
 */

/** The four families a report is filed under (`AM-470`): Operativos, Horas, Costos and Facturación. */
export const REPORT_FAMILIES = ['operational', 'hours', 'costs', 'billing'] as const;

export type ReportFamily = (typeof REPORT_FAMILIES)[number];

/** `ReportParameterSummary`: one filter a report accepts; `values` only travels for an `enum`. */
export interface ReportParameter {
  name: string;
  type: 'uuid' | 'enum' | 'string';
  values?: string[] | null;
  /** `AM-720`: the filter of an identifier also takes `none`, the rows with none ("Sin contrato", "Sin servicio"). */
  acceptsNone?: boolean;
}

/** `ReportColumnSummary`: one column of a reading, with the es-AR label the server owns (R-07). */
export interface ReportColumn {
  key: string;
  label: string;
  type:
    | 'string'
    | 'integer'
    | 'decimal'
    | 'money'
    | 'percent'
    | 'date'
    | 'datetime'
    | 'uuid'
    | 'minutes'
    // `AM-813` (decision 42 of the 52): the code of a closed list, for integrations; never on screen.
    | 'code';
}

/** `ReportSummary`: one entry of the catalogue - everything the console needs to offer a report. */
export interface ReportSummary {
  key: string;
  family: ReportFamily;
  title: string;
  permission: string;
  exports: string[];
  parameters: ReportParameter[];
  groupBy: string[];
  columns: ReportColumn[];
  /** `AM-543`: the dimension a report that is always read grouped falls back on; null when it has a reading row by row. */
  defaultGroupBy?: string | null;
}

export interface ReportCatalogResponse {
  items: ReportSummary[];
}

/** One row of a reading: every value keeps the type `ReportCsv.Format` would print it as (AM-130 point 4). */
export type ReportRow = Record<string, string | number | boolean | null>;

/** `ReportResponse`: the answer of one report over one window, one set of dimensions and filters. */
export interface ReportResponse {
  reportKey: string;
  from: string;
  to: string;
  parameters: Record<string, string | null>;
  groupBy: string[];
  columns: ReportColumn[];
  rows: ReportRow[];
  rowCount: number;
  truncated: boolean;
  generatedAt: string;
  notes: string[];
  /** `AM-387`: the minutes said under the rows, over the whole range (`hours`, `rejectedHours`). */
  totals?: Record<string, number> | null;
  /**
   * `AM-471`: the words of the notes that have them, by code (`estimate_only`, `amounts_without_vat`),
   * the same the files of the report write above their table.
   */
  noteTexts?: Record<string, string> | null;
}

/** What `reportsApi.run`/`csvUrl` take: the window, the dimensions and the report's own filters. */
export interface ReportParams {
  from?: string;
  to?: string;
  groupBy?: string[];
  filters?: Record<string, string>;
}

function reportQuery(params: ReportParams, extra: Record<string, string> = {}): string {
  const search = new URLSearchParams();
  if (params.from !== undefined) {
    search.set('from', params.from);
  }
  if (params.to !== undefined) {
    search.set('to', params.to);
  }
  if (params.groupBy !== undefined && params.groupBy.length > 0) {
    search.set('groupBy', params.groupBy.join(','));
  }
  for (const [name, value] of Object.entries(params.filters ?? {})) {
    if (value.length > 0) {
      search.set(name, value);
    }
  }
  for (const [name, value] of Object.entries(extra)) {
    search.set(name, value);
  }
  const text = search.toString();
  return text.length === 0 ? '' : `?${text}`;
}

/**
 * How the customer's monthly summary breaks the hours down (`hoursDetail`): by work type (and by
 * service), the default, or without that breakdown. The total of the month and the per-ticket detail
 * of the approved hours are printed either way.
 */
export type CustomerSummaryHoursDetail = 'workType' | 'none';

/**
 * Which tickets the file of the tickets of a customer lists (`status`): the open ones, the rest, or
 * all of them, the same three the tab Tickets of the customer's file offers.
 */
export type CustomerTicketsState = 'all' | 'open' | 'closed';

function customerTicketsQuery(
  organizationId: string,
  status: CustomerTicketsState,
  month: string | null,
  format: 'pdf' | 'xlsx',
): string {
  const query = new URLSearchParams({ organizationId, status });
  if (month !== null) {
    query.set('month', month);
  }
  if (format === 'xlsx') {
    query.set('format', 'xlsx');
  }
  return query.toString();
}

export const reportsApi = {
  list: (signal?: AbortSignal): Promise<ReportCatalogResponse> =>
    apiFetch<ReportCatalogResponse>('/api/v1/reports', signal === undefined ? {} : { signal }),
  run: (key: string, params: ReportParams, signal?: AbortSignal): Promise<ReportResponse> =>
    apiFetch<ReportResponse>(
      `/api/v1/reports/${key}${reportQuery(params)}`,
      signal === undefined ? {} : { signal },
    ),
  /** The URL `downloadFile` (`shared/api/http.ts`) fetches for the `format=csv` export (FR-327). */
  csvUrl: (key: string, params: ReportParams): string =>
    `/api/v1/reports/${key}${reportQuery(params, { format: 'csv' })}`,
  /** The URL `downloadFile` fetches for the `format=xlsx` export, a workbook Excel opens as it is (AM-172). */
  xlsxUrl: (key: string, params: ReportParams): string =>
    `/api/v1/reports/${key}${reportQuery(params, { format: 'xlsx' })}`,
  /**
   * The URL `downloadFile` fetches for the customer's monthly summary: the PDF of one organization
   * over one month (`YYYY-MM`) of the tenant's calendar, generated on each request, with the hours
   * broken down by work type (and by service when some hour has one) or not broken down.
   */
  customerSummaryUrl: (
    organizationId: string,
    month: string,
    hoursDetail: CustomerSummaryHoursDetail,
  ): string =>
    `/api/v1/reports/customer-summary?${new URLSearchParams({ organizationId, month, hoursDetail }).toString()}`,
  /**
   * The URL `downloadFile` fetches for the customer's monthly summary as a workbook (`format=xlsx`):
   * the same organization and month as the PDF, in two sheets (Resumen, the tickets with their hours,
   * and Detalle, one row per approved hour). It carries no `hoursDetail`, because the workbook always
   * has every hour one by one.
   */
  customerSummaryXlsxUrl: (organizationId: string, month: string): string =>
    `/api/v1/reports/customer-summary?${new URLSearchParams({ organizationId, month, format: 'xlsx' }).toString()}`,
  /**
   * The URL `downloadFile` fetches for the file of the tickets of a customer as a PDF (`AM-343`): one
   * row per ticket with the filter of the tab Tickets, `month` (`YYYY-MM`) being `null` for every month.
   */
  customerTicketsUrl: (
    organizationId: string,
    status: CustomerTicketsState,
    month: string | null,
  ): string =>
    `/api/v1/reports/customer-tickets?${customerTicketsQuery(organizationId, status, month, 'pdf')}`,
  /** The same file as a workbook of one sheet (`format=xlsx`). */
  customerTicketsXlsxUrl: (
    organizationId: string,
    status: CustomerTicketsState,
    month: string | null,
  ): string =>
    `/api/v1/reports/customer-tickets?${customerTicketsQuery(organizationId, status, month, 'xlsx')}`,
};
